import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Paperclip, X, Loader2 } from "lucide-react";

/** Feature 2: modale "Segnala un problema" — Testo obbligatorio + screenshot opzionale.
 * Il resto (pagina, regime, UA, ultimo errore) è raccolto automaticamente.
 */
export function FeedbackModal({ open, onClose }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [testo, setTesto] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g)$/i.test(f.type)) {
      toast.error("Formato non supportato — solo JPG o PNG");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Screenshot troppo grande (max 5MB)");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const clearFile = () => {
    setFile(null);
    setPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const reset = () => {
    setTesto("");
    clearFile();
  };

  const submit = async () => {
    if (!testo.trim()) {
      toast.error("Descrivi cosa non va prima di inviare");
      return;
    }
    setLoading(true);
    try {
      // Ultima API fallita (se il resto dell'app la salva su window.__lastFailedApi)
      const lastFailedApi = typeof window !== "undefined" ? (window.__lastFailedApi || "") : "";
      await api.post("/feedback", {
        testo: testo.trim(),
        pagina: pathname,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        last_failed_api: lastFailedApi,
        screenshot_b64: preview || "",
      });
      toast.success("Grazie! Segnalazione ricevuta 🙏", { id: "feedback-sent" });
      reset();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile inviare la segnalazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) { reset(); onClose?.(); } }}>
      <DialogContent className="sm:max-w-lg" data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Segnala un problema</DialogTitle>
          <DialogDescription>
            Descrivi cosa ti stava succedendo. Ci aiuta a sistemare le cose in fretta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="Cosa non va? Descrivi cosa stavi facendo e cosa ti aspettavi."
            className="min-h-[120px] rounded-xl resize-none"
            maxLength={5000}
            data-testid="feedback-testo"
          />

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={onFile}
              className="hidden"
              data-testid="feedback-file-input"
            />
            {!preview ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => fileInputRef.current?.click()}
                data-testid="feedback-attach-btn"
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Allega screenshot (opzionale)
              </Button>
            ) : (
              <div className="relative inline-block">
                <img src={preview} alt="screenshot" className="max-h-32 rounded-lg border" />
                <button
                  type="button"
                  onClick={clearFile}
                  className="absolute -top-2 -right-2 bg-secondary text-secondary-foreground rounded-full w-6 h-6 flex items-center justify-center shadow"
                  data-testid="feedback-clear-file"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Verranno inclusi automaticamente: pagina corrente ({pathname}) e dati del tuo profilo ({user?.email}).
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose?.(); }} disabled={loading} data-testid="feedback-cancel">
            Annulla
          </Button>
          <Button onClick={submit} disabled={loading || !testo.trim()} data-testid="feedback-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {loading ? "Invio…" : "Invia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
